import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Pencil, Plus, ShoppingBasket, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: '', quantity: '', unit: '' };

const ShoppingList = ({ departmentId, onClose }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/departments/${departmentId}/shopping-items`);
      setItems(response.data || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Die Einkaufsliste konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [departmentId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const payloadFromForm = () => ({
    name: form.name.trim(),
    quantity: form.quantity === '' ? null : Number(form.quantity),
    unit: form.unit.trim() || null,
  });

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      const payload = payloadFromForm();
      const response = editingId
        ? await axios.put(`${API}/departments/${departmentId}/shopping-items/${editingId}`, payload)
        : await axios.post(`${API}/departments/${departmentId}/shopping-items`, payload);
      setItems(current => editingId
        ? current.map(item => item.id === editingId ? response.data : item)
        : [...current, response.data]);
      setForm(emptyForm);
      setEditingId(null);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Der Artikel konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      quantity: item.quantity ?? '',
      unit: item.unit || '',
    });
  };

  const remove = async (item) => {
    setError('');
    try {
      await axios.delete(`${API}/departments/${departmentId}/shopping-items/${item.id}`);
      setItems(current => current.filter(candidate => candidate.id !== item.id));
      if (editingId === item.id) {
        setEditingId(null);
        setForm(emptyForm);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Der Artikel konnte nicht entfernt werden.');
    }
  };

  return (
    <section className="w-full rounded-2xl bg-white shadow-xl" aria-label="Einkaufsliste">
      <header className="flex items-start justify-between border-b border-gray-100 p-4 sm:p-6">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <ShoppingBasket className="h-5 w-5 shrink-0 text-gray-700" aria-hidden="true" />
            <span>Einkaufsliste</span>
          </h2>
          <p className="mt-1 text-sm text-gray-500">Gemeinsam für die gesamte Wachabteilung</p>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Einkaufsliste schließen" className="flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-gray-100">
            <X />
          </button>
        )}
      </header>

      <form onSubmit={submit} className="grid gap-3 border-b border-gray-100 p-4 sm:grid-cols-[minmax(0,1fr)_7rem_9rem_auto] sm:p-6">
        <input name="shopping-name" value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} placeholder="Was wird benötigt?" className="min-h-11 rounded-xl border border-gray-300 px-3" required />
        <input name="shopping-quantity" value={form.quantity} onChange={event => setForm(current => ({ ...current, quantity: event.target.value }))} type="number" min="0" step="any" placeholder="Menge" className="min-h-11 rounded-xl border border-gray-300 px-3" />
        <input name="shopping-unit" value={form.unit} onChange={event => setForm(current => ({ ...current, unit: event.target.value }))} placeholder="Einheit" className="min-h-11 rounded-xl border border-gray-300 px-3" />
        <button type="submit" disabled={saving} className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gray-800 px-4 font-semibold text-white hover:bg-gray-900 disabled:opacity-50">
          <Plus className="h-4 w-4" /> {editingId ? 'Speichern' : 'Hinzufügen'}
        </button>
      </form>

      <div className="p-4 sm:p-6">
        {error && <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}
        {loading ? (
          <p className="py-8 text-center text-gray-500">Einkaufsliste wird geladen …</p>
        ) : items.length === 0 ? (
          <p className="py-10 text-center font-medium text-gray-500">Aktuell ist alles eingekauft</p>
        ) : (
          <ul className="space-y-3">
            {items.map(item => (
              <li key={item.id} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                <button type="button" onClick={() => remove(item)} aria-label={`${item.name} als eingekauft markieren`} className="flex min-h-11 min-w-11 items-center justify-center rounded-full border-2 border-gray-300 text-gray-600 hover:bg-gray-100">
                  <Check className="h-5 w-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-900">{item.name}</span>
                    {item.source === 'automatic' && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">Automatisch berechnet</span>}
                    {item.manual_override && item.source === 'automatic' && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">Manuell angepasst</span>}
                  </div>
                  {(item.quantity !== null && item.quantity !== undefined) && <p className="text-sm text-gray-600">{item.quantity}{item.unit ? ` ${item.unit}` : ''}</p>}
                </div>
                <button type="button" onClick={() => edit(item)} aria-label={`${item.name} bearbeiten`} className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100">
                  <Pencil className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default ShoppingList;
